import { WallDatumAbstractionProvider } from "building-generation/elements/datums/datumAbstractionProviders";
import { levelName, sortedLevels } from "building-generation/elements/level/levelTools";
import {
  collectWallFinishFaces,
  collectWallFinishFaces2,
} from "building-generation/elements/wallAssemblies/wallFinishFaces";
import type { Building } from "building-types/building/buildingTypes";
import { type TraversedWall, WallAreaType } from "building-types/dataSequences/builtin/wallAreaTraversalTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import { ensureDefined } from "common/utils/assertOfType";
import { inchesSquaredNew } from "common/utils/nominalNumbers";
import objectFilter from "common/utils/objectFilter";
import { facesArea } from "geom/brep";

export function* traverseWallAreas(building: Building): Iterator<TraversedWall> {
  const {
    walls,
    roofSystems: roofs,
    dormers,
    wainscoting,
    levels,
    levelsAttributes,
    settings,
    wallSweeps,
    interiorPalette,
    exteriorPalette,
    mirror,
    rooms,
    datumAbstractions,
    enabledFeatures,
  } = building;
  const { wall: wallSettings, wainscoting: wainscotingSettings, wallCatalog: wallCatalogSettings } = settings;
  const { exteriorWallMaterial } = wallSettings;

  let garageWallArea = 0;

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);

  for (const level of sortedLevels(levels)) {
    const levelId = level.id;
    const levelKey = ensureDefined(levelName(levels[levelId]));
    const levelWallSystem = walls[levelId];
    const levelWainscoting = objectFilter(wainscoting, (x) => x.levelId === levelId);
    const levelWallSweeps = wallSweeps[levelId];

    const wallFinishFaces =
      enabledFeatures.wallPlanes || enabledFeatures.collectWallFinishFaces2
        ? collectWallFinishFaces2({
            wallDatumAbstractionProvider,
            wallSystem: levelWallSystem,
            wallSweeps: levelWallSweeps,
            wallCatalogSettings,
            mirror,
          })
        : collectWallFinishFaces({
            wallDatumAbstractionProvider,
            wallSystem: levelWallSystem,
            wallSweeps: levelWallSweeps,
            interiorPalette,
            exteriorPalette,
            mirror,
            wallSettings,
            wallCatalogSettings,
          });

    const { exteriorWallFaces, interiorWallFaces } = wallFinishFaces;

    for (const blockId in exteriorWallFaces) {
      for (const facesWithMaterial of exteriorWallFaces[blockId]) {
        const { faces, material } = facesWithMaterial;
        const materialArea = facesArea(faces);
        yield {
          wallType: WallAreaType.EXTERIOR_WALL,
          area: inchesSquaredNew(materialArea),
          exteriorMaterial: material as ExteriorMaterial,
          detail: levelKey ?? "",
        };
      }
    }

    let intWallArea = 0;
    for (const roomId in interiorWallFaces) {
      const room = rooms[roomId];
      for (const facesWithMaterial of interiorWallFaces[roomId]) {
        const { faces } = facesWithMaterial;
        const materialArea = facesArea(faces);
        if (room && room.attributes.roomClass === RoomClass.GARAGE) {
          garageWallArea += materialArea;
        } else {
          intWallArea += materialArea;
        }
      }
    }
    if (intWallArea > 0) {
      yield {
        wallType: WallAreaType.INTERIOR_WALL,
        area: inchesSquaredNew(intWallArea),
        detail: levelKey,
      };
    }

    for (const wainscotingId in levelWainscoting) {
      const currentWainscoting = levelWainscoting[wainscotingId];
      const wainscotingArea = facesArea(currentWainscoting.faces);
      yield {
        wallType: WallAreaType.EXTERIOR_WALL,
        area: inchesSquaredNew(wainscotingArea),
        exteriorMaterial: wainscotingSettings.material ?? "masonry0",
        detail: levelKey,
      };
    }
  }

  for (const roofId in roofs) {
    const roof = roofs[roofId];
    const { roofs: subRoofs } = roof;

    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const { levelId, roofWallSystem } = subRoof;
      if (!roofWallSystem) {
        continue;
      }

      const { wallSystem, levels: roofLevels, levelsAttributes: roofLevelsAttributes } = roofWallSystem;
      const roofWallSystemDatumAbstractionProvider = new WallDatumAbstractionProvider(
        roofLevels,
        roofLevelsAttributes,
        datumAbstractions,
      );

      const levelWallSweeps = wallSweeps[levelId];
      const wallFinishFaces =
        enabledFeatures.wallPlanes || enabledFeatures.collectWallFinishFaces2
          ? collectWallFinishFaces2({
              wallDatumAbstractionProvider: roofWallSystemDatumAbstractionProvider,
              wallSystem,
              wallSweeps: levelWallSweeps,
              wallCatalogSettings,
              mirror,
            })
          : collectWallFinishFaces({
              wallDatumAbstractionProvider: roofWallSystemDatumAbstractionProvider,
              wallSystem,
              wallSweeps: levelWallSweeps,
              interiorPalette,
              exteriorPalette,
              mirror,
              wallSettings,
              wallCatalogSettings,
            });

      const { exteriorWallFaces } = wallFinishFaces;
      for (const blockId in exteriorWallFaces) {
        for (const facesWithMaterial of exteriorWallFaces[blockId]) {
          const { faces, material } = facesWithMaterial;
          const materialArea = facesArea(faces);
          yield {
            wallType: WallAreaType.EXTERIOR_WALL,
            area: inchesSquaredNew(materialArea),
            exteriorMaterial: material as ExteriorMaterial,
            detail: "Gable Wall",
          };
        }
      }
    }

    for (const dormerId in dormers) {
      if (dormers[dormerId].hostRoofSystemId !== roof.id) {
        continue;
      }
      const { wallMaterial, wall } = dormers[dormerId];
      const material = wallMaterial ?? exteriorWallMaterial;
      yield {
        wallType: WallAreaType.EXTERIOR_WALL,
        area: inchesSquaredNew(facesArea(wall)),
        exteriorMaterial: material,
        detail: "Dormer Wall",
      };
    }
  }

  yield { wallType: WallAreaType.GARAGE_WALL, area: inchesSquaredNew(garageWallArea) };
}
